import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, Trophy } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * Calibration Streak Widget — reinforces the AI Negotiator coaching habit
 * by tracking consecutive weeks with an average calibration score >= 7/10.
 *
 * Renders on the Dashboard next to the KPI grid. Auto-hides on fresh
 * accounts (no reviews yet) so it doesn't clutter the empty state.
 *
 * Layout:
 *   [🔥 3-week streak · 7.4/10 avg]  [<sparkline 8w>]  [best: 4]
 */
export default function CalibrationStreakWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/negotiation-reviews/streak")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(e?.response?.data?.detail || "Streak fetch failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-3" data-testid="streak-widget-loading">
        <div className="animate-pulse text-neutral-500 text-xs">Loading calibration streak…</div>
      </div>
    );
  }
  if (!data) return null;

  // Auto-hide when the pilot has literally no coaching history — this widget
  // is meant to reinforce a habit, not create anxiety on new accounts.
  const totalReviews = (data.weeks_history || []).reduce((sum, w) => sum + (w.review_count || 0), 0);
  if (totalReviews === 0) return null;

  const streak = data.current_streak_weeks || 0;
  const longest = data.longest_streak_weeks || 0;
  const thisWeekAvg = data.this_week_avg_score;

  // Tone based on THIS-week avg (or last populated week if this week is quiet)
  const activeAvg = thisWeekAvg ?? [...(data.weeks_history || [])]
    .reverse().find(w => w.avg_score != null)?.avg_score;
  const tone = activeAvg == null
    ? "border-white/10 bg-white/[0.02] text-neutral-400"
    : activeAvg >= 7
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
      : activeAvg >= 5
        ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
        : "border-rose-500/40 bg-rose-500/10 text-rose-300";

  const streakLabel = streak === 0
    ? (activeAvg != null ? "Rebuild your streak" : "Get started")
    : `${streak}-week streak`;

  return (
    <div className={`card-surface p-4 border ${tone}`} data-testid="streak-widget">
      <div className="flex items-center gap-4 flex-wrap">
        {/* Streak headline */}
        <div className="flex items-center gap-2.5 min-w-0">
          <Flame
            size={22}
            weight={streak > 0 ? "fill" : "regular"}
            className={streak > 0 ? "text-[#FF5A1F]" : "text-neutral-500"}
          />
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 leading-none">
              Calibration Streak
            </div>
            <div className="text-sm font-semibold text-white tabular-nums mt-0.5" data-testid="streak-headline">
              {streakLabel}
              {thisWeekAvg != null && (
                <span className="text-neutral-400 font-normal">
                  {" · "}
                  <span className={activeAvg >= 7 ? "text-emerald-300" : activeAvg >= 5 ? "text-amber-300" : "text-rose-300"}>
                    {thisWeekAvg}/10
                  </span>
                  {" this week"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Sparkline */}
        <div className="ml-auto flex items-center gap-3 flex-1 min-w-[180px] justify-end">
          <Sparkline history={data.weeks_history || []} />
          {longest > 0 && longest !== streak && (
            <div
              className="inline-flex items-center gap-1 shrink-0 rounded-full border border-white/10 bg-white/[0.03] text-neutral-400 px-2 py-0.5 text-[10px] tabular-nums"
              title="All-time best streak"
              data-testid="streak-longest"
            >
              <Trophy size={9} weight="fill" />
              Best {longest}w
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function Sparkline({ history }) {
  if (!history?.length) return null;
  const bars = history.slice(-8);
  return (
    <div className="flex items-end gap-1 h-8" data-testid="streak-sparkline">
      {bars.map((w, i) => {
        const avg = w.avg_score;
        // Height mapping — score 1-10 → 4px-32px
        const height = avg == null ? 4 : Math.max(4, Math.round((avg / 10) * 32));
        const barTone = avg == null
          ? "bg-white/10"
          : avg >= 7
            ? "bg-emerald-400"
            : avg >= 5
              ? "bg-amber-400"
              : "bg-rose-400";
        // Emphasize the current week (last bar)
        const isCurrent = i === bars.length - 1;
        const title = avg == null
          ? `Week of ${w.week_start}: no reviews`
          : `Week of ${w.week_start}: ${avg}/10 avg (${w.review_count} bid${w.review_count === 1 ? "" : "s"})`;
        return (
          <div
            key={w.week_start}
            style={{ height: `${height}px` }}
            className={`w-2 rounded-sm ${barTone} ${isCurrent ? "ring-1 ring-white/30" : ""}`}
            title={title}
          />
        );
      })}
    </div>
  );
}
